import React, { useEffect, useMemo, useState } from 'react';
import { fetchAuthorizedTechServices } from '../services/serviceAreasService';
import { decodeHtml } from '../utils/security';

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   districtId: number | null,
 *   districtLabel: string,
 *   mainCategories: object[],
 * }} props
 */
export function TechServicesMapDistrictModal({
  open,
  onClose,
  districtId,
  districtLabel,
  mainCategories = [],
}) {
  const roots = useMemo(() => {
    const list = Array.isArray(mainCategories) ? mainCategories : [];
    return [...list]
      .filter((c) => !c.ustKategoriId)
      .sort((a, b) => (a.kategoriSirasi ?? 0) - (b.kategoriSirasi ?? 0));
  }, [mainCategories]);

  const [selectedKategoriId, setSelectedKategoriId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [servis, setServis] = useState(null);

  useEffect(() => {
    if (!open || roots.length === 0) {
      setSelectedKategoriId(null);
      return;
    }
    setSelectedKategoriId(String(roots[0].id || roots[0]._id));
  }, [open, districtId, roots]);

  useEffect(() => {
    if (!open || !districtId || !selectedKategoriId) {
      setServis(null);
      setError('');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setServis(null);

    fetchAuthorizedTechServices({ districtId, kategoriId: selectedKategoriId })
      .then((res) => {
        if (cancelled) return;
        const list = res?.veri?.teknikServisler || [];
        setServis(Array.isArray(list) && list.length > 0 ? list[0] : null);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e.status === 404) {
          setServis(null);
          setError('');
        } else {
          setError(e.message || 'Servis bilgisi alınamadı');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, districtId, selectedKategoriId]);

  if (!open) return null;

  const selectedCat = roots.find(
    (c) => String(c.id || c._id) === String(selectedKategoriId)
  );
  const selectedLabel = selectedCat?.kategoriAdi || selectedCat?.ad || '';

  const ad = servis?.ad ? decodeHtml(servis.ad) : null;

  return (
    <div
      className="tech-services-map-page__modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tech-map-district-modal-title"
      onClick={onClose}
    >
      <div className="tech-services-map-page__modal" onClick={(e) => e.stopPropagation()}>
        <div className="tech-services-map-page__modal-header">
          <h2 id="tech-map-district-modal-title">İlçe Yetkili Servisleri</h2>
          <button
            type="button"
            className="modal-close-btn tech-services-map-page__modal-close"
            onClick={onClose}
            aria-label="Kapat"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="tech-services-map-page__modal-body">
          <p className="tech-services-map-page__modal-district">
            <strong>{districtLabel || 'İlçe'}</strong>
          </p>

          {roots.length === 0 ? (
            <p className="tech-services-map-page__modal-empty">Ürün kategorileri yüklenemedi.</p>
          ) : (
            <>
              <div className="tech-services-map-page__modal-cat-label">Ürün ana kategorisi</div>
              <div className="tech-services-map-page__modal-cat-pills" role="tablist">
                {roots.map((cat) => {
                  const id = String(cat.id || cat._id);
                  const active = id === String(selectedKategoriId);
                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`tech-services-map-page__modal-cat-pill${
                        active ? ' tech-services-map-page__modal-cat-pill--active' : ''
                      }`}
                      onClick={() => setSelectedKategoriId(id)}
                    >
                      {cat.kategoriAdi || cat.ad}
                    </button>
                  );
                })}
              </div>

              {loading && (
                <div className="tech-services-map-page__modal-loading">
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  <span>Yükleniyor…</span>
                </div>
              )}

              {!loading && error && (
                <div className="tech-services-map-page__modal-error" role="alert">
                  {error}
                </div>
              )}

              {!loading && !error && !servis && (
                <p className="tech-services-map-page__modal-empty">
                  <strong>{selectedLabel}</strong> için bu ilçede kayıtlı yetkili teknik servis bulunamadı.
                </p>
              )}

              {!loading && !error && servis && (
                <div className="tech-services-map-page__modal-service">
                  <p className="tech-services-map-page__modal-service-kicker">
                    <strong>{selectedLabel}</strong> yetkili servis
                  </p>
                  <dl className="tech-services-map-page__modal-dl">
                    <div>
                      <dt>Ad</dt>
                      <dd>{ad || '—'}</dd>
                    </div>
                    {servis.kod ? (
                      <div>
                        <dt>Kod</dt>
                        <dd>{servis.kod}</dd>
                      </div>
                    ) : null}
                    {servis.telefon ? (
                      <div>
                        <dt>Telefon</dt>
                        <dd>
                          <a href={`tel:${servis.telefon}`}>{servis.telefon}</a>
                        </dd>
                      </div>
                    ) : null}
                    {servis.mail ? (
                      <div>
                        <dt>E-posta</dt>
                        <dd>
                          <a href={`mailto:${servis.mail}`}>{servis.mail}</a>
                        </dd>
                      </div>
                    ) : null}
                    {servis.yetkiliKisi ? (
                      <div>
                        <dt>Yetkili kişi</dt>
                        <dd>{decodeHtml(servis.yetkiliKisi)}</dd>
                      </div>
                    ) : null}
                    {servis.yetkiliTelefon ? (
                      <div>
                        <dt>Yetkili telefon</dt>
                        <dd>
                          <a href={`tel:${servis.yetkiliTelefon}`}>{servis.yetkiliTelefon}</a>
                        </dd>
                      </div>
                    ) : null}
                    {servis.adres ? (
                      <div>
                        <dt>Adres</dt>
                        <dd>{decodeHtml(servis.adres)}</dd>
                      </div>
                    ) : null}
                  </dl>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
